<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    class="mt-5 mb-5 pt-3 pb-5 pl-3 pr-3"
  >
    <v-dialog v-model="showValidationDialog">
          <v-card class="search-modal">
          <h2>Validation Results</h2>
          <v-form class="search-form">
              <v-alert v-if="!!validationResult && !!validationResult.errors && validationResult.errors.length > 0" type="error">  
                <pre>{{ validationResult.errors.join("\r\n") }}</pre>
              </v-alert>  
              <v-alert v-if="!!validationResult && validationResult.warnings && validationResult.warnings.length > 0" type="warning" outlined> 
                <pre>{{ validationResult.warnings.join("\r\n") }}</pre>
              </v-alert>  
              <v-btn
                      large
                      color="primary"
                      width="125px"
                      :disabled="!validationResult.canBeSaved"
                      v-on:click="saveChanges"
                  >
                  <v-icon v-if="!validationResult.canBeSaved">mdi-alert</v-icon>
                  Save</v-btn>     
          </v-form>
          </v-card>
    </v-dialog>
    <div class="profile-content">
      <v-dialog v-model="showReportDialog">
        <report-viewer v-if="showReportDialog"
          :reportName="Report_Name"
          :dataUrl="Report_URL"
          ref="reportViewer"
          :printOnly="true"
        />   
      </v-dialog>       
      <v-row>
        <h1>
          Kennel ID #{{ kennelId }} {{ kennelInfo.kennelName }}
          <v-chip
            v-if="kennelInfo.permanentDate != null"
            text-color="white"
            class="ma-1"
            color="blue"
            >Permanent</v-chip
          >
          <v-chip
            v-if="kennelInfo.isHallOfFame"
            text-color="white"
            class="ma-1"
            color="orange"
            >HOF</v-chip
          >
          <v-chip
            v-if="kennelInfo.isHallOfFameExcellent"
            text-color="white"
            class="ma-1"
            color="orange"
            >HOFX</v-chip
          >
          <v-chip
            v-if="kennelInfo.isStockdogExcellent"
            text-color="white"
            class="ma-1"
            color="orange"
            >SKE</v-chip
          >
        </h1>
        <v-spacer/>
        <v-btn color="primary"  class="mr-2" v-on:click="showRegCertDialog()" :disabled="!kennelId">
          Registration Certificate
        </v-btn>
        <v-btn disabled color="primary" class="mr-2">
          Owners Change Registration
        </v-btn>
      </v-row>
      <v-row>           
        <v-textarea
          :disabled="!isEditMode"
          :visible="!isEditMode"
          outlined
          auto-grow
          clearable
          rows="1"
          v-model="kennelInfo.adminNotes"
          label="Description of changes (required)"
        />
        <v-btn class="ml-1 mr-1" 
          :disabled="isEditMode && (!kennelInfo.adminNotes || /^\s*$/.test(kennelInfo.adminNotes))"
          color="primary" 
          @click="toggleEditMode">
          {{ isEditMode ? "Save" : "Edit" }}
        </v-btn>
      </v-row>
      <v-row>
        <v-text-field
          v-model="kennelInfo.kennelName"
          label="Kennel Name"
          :disabled="!isEditMode"
          outlined
          :rules="[v => v.length <= 50 || 'Max 50 characters']"
          counter="50"
        ></v-text-field>
      </v-row>
      <v-row v-if="isEditMode">        
        <KennelSimilarNamesDialog :kennelSearchPhrase="kennelInfo.kennelName" />
      </v-row>
      <v-row class="mt-5">
        <v-col cols="2">
          <DatepickerFormatted
            :disabled="!isEditMode"
            :date="kennelInfo.startDate"
            label="Start Date"
            v-on:updateDates="updateDates($event, 'startDate')"
          />
          <DatepickerFormatted
            :disabled="!isEditMode"
            :date="kennelInfo.registrationDate"
            label="Registration Date"
            v-on:updateDates="updateDates($event, 'registrationDate')"
          />
          <DatepickerFormatted
            :disabled="!isEditMode"
            :date="kennelInfo.permanentDate"
            label="Permanent Date"
            v-on:updateDates="updateDates($event, 'permanentDate')"
          />
        </v-col>
        <v-col cols="2">
          <DatepickerFormatted
            :disabled="!isEditMode"
            :date="kennelInfo.postDate"
            label="Post Date"
            v-on:updateDates="updateDates($event, 'postDate')"
          />
          <DatepickerFormatted
            :disabled="!isEditMode"
            :date="kennelInfo.expirationDate"
            label="Expiration Date"
            v-on:updateDates="updateDates($event, 'expirationDate')"
          />
        </v-col>
        <v-col cols="2">
          <a v-if="!!kennelId" 
            :href="`/admin/titling/kennel/review?kennelId=${encodeURIComponent(kennelId.trim())}`"
            target="_blank"
            >Titles review (experimental)</a>
          <v-checkbox
            v-model="kennelInfo.isHallOfFame"
            :disabled="!isEditMode"
            label="Hall of Fame"
            :value="kennelInfo.isHallOfFame"
          ></v-checkbox>
          <DatepickerFormatted
            :disabled="!isEditMode"
            :date="kennelInfo.hallOfFameDate"
            label="Hall of Fame Date"
            v-on:updateDates="updateDates($event, 'hallOfFameDate')"
          />
          <v-checkbox
            v-model="kennelInfo.isHallOfFameExcellent"
            :disabled="!isEditMode"
            label="Hall of Fame Excellent"
            :value="kennelInfo.isHallOfFameExcellent"
          ></v-checkbox>
          <DatepickerFormatted
            :disabled="!isEditMode"
            :date="kennelInfo.hallOfFameExcellentDate"
            label="Hall of Fame Excellent Date"
            v-on:updateDates="updateDates($event, 'hallOfFameExcellentDate')"
          />
          <v-checkbox
            v-model="kennelInfo.isStockdogExcellent"
            :disabled="!isEditMode"
            label="Stockdog Excellent"
            :value="kennelInfo.isStockdogExcellent"
          ></v-checkbox>
          <DatepickerFormatted
            :disabled="!isEditMode"
            :date="kennelInfo.stockdogExcellentDate"
            label="Stockdog Excellent Date"
            v-on:updateDates="updateDates($event, 'stockdogExcellentDate')"
          />
        </v-col>  
        <v-col>
          <AdminEditableCoownersList
            :isEditMode="isEditMode"
            :owners="kennelInfo.owners"
            :ownershipType="kennelOwnershipType"
            v-on:updateSelectedPeople="updateSelectedPeople($event)"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-textarea
          disabled
          @input="updateText('notes', $event)"
          :value="kennelInfo.notes"
          outlined
          rows="1"
          auto-grow
          label="Notes"
        ></v-textarea>
      </v-row>   
      <v-snackbar v-model="showInfoMessage" title color="green">
        {{ infoMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn color="primary" text v-bind="attrs" @click="showInfoMessage = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
      <v-snackbar v-model="showErrorMessage" title color="red">
        {{ errorMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn color="primary" text v-bind="attrs" @click="showErrorMessage = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>      
    </div>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import ReportViewer from "@/components/Admin/Reports/ReportViewer.vue";
import AdminEditableCoownersList, { OwnershipType } from "@/components/Admin/AdminEditableCoownersList.vue";
import KennelSimilarNamesDialog from "@/components/Admin/KennelSimilarNamesDialog.vue";
import DatepickerFormatted from "@/components/common/inputs/DatepickerFormatted.vue";

import kennelService from "@/services/admin/kennelService";

import { UserInfo } from "@/types/User";
import { Messages } from "@/constants";

export default Vue.extend({
  name: "KennelDetails",
  components: {
    DatepickerFormatted,
    AdminEditableCoownersList,
    ReportViewer,
    KennelSimilarNamesDialog
  },
  data: function () {
    return {
      kennelInfo: { kennelName: "" } as any,
      isEditMode: false,
      Report_Name: 'Asca.Api.Reports.rpt3210_Dog_Reg_Certificate.rdlx',
      Report_URL: '`/reports/registration/${dogId}`',
      showReportDialog: false,
      showValidationDialog: false,
      validationResult: {} as any,
      errorMessage: '',
      infoMessage: '',
      showErrorMessage: false,
      showInfoMessage: false
    };
  },
  methods: {
    showRegCertDialog: function (){   
      this.Report_Name = "Asca.Api.Reports.rpt6210_Kennel_Reg_Certificate.rdlx";
      this.Report_URL = "/reports/kennelregistration/"+this.kennelId;
      this.showReportDialog = true;
    },
      reloadData: function () {
          this.isEditMode = false;
      if (this.kennelId) {
        kennelService
          .getKennelById(this.kennelId)
          .then((res) => {
            this.kennelInfo = res;
          })
          .catch((error) => {
            this.kennelInfo = { kennelName: "" };
            this.$handleError(error);
          });
      } else {
          this.isEditMode = true;
        this.kennelInfo = { kennelName: "" };
      }
      
      this.showValidationDialog = false;
      this.errorMessage = '';
      this.showErrorMessage = false;
      this.validationResult= {};
    },
    updateText: function (key: string, event: string) {
      this.$data.kennelInfo[key] = event;
    },
    validateAndConfirmSavingChanges: function() { 
      kennelService
        .validateKennelEdit(this.kennelInfo)
        .then((res: any) => {
          this.validationResult = res;
          this.showValidationDialog = true;
        })     
    },
    saveChanges: function () {
      this.showValidationDialog = false;
      this.$store.dispatch("changeLoaderStatus",true);
      kennelService
                .editKennelById(this.kennelInfo)
                .then((res) => {
                  if(res.success){
                    if(res.id && res.id != this.$props.kennelId){
                      this.$navigate({ path: `/admin/kennels/${res.id}` });
                    }
                    this.reloadData();
                    this.infoMessage = Messages.SaveSuccessful;
                    this.showInfoMessage = true;
                  } else {              
                    this.errorMessage = Messages.SaveError + " " + res.messages.join(', ');
                    this.showErrorMessage = true;
                  }
                })
                .catch((e) => {                  
                  this.errorMessage = Messages.SaveError;
                   this.showErrorMessage = true;
                })
                .finally(() => this.$store.dispatch("changeLoaderStatus",false));
    },
    toggleEditMode: function () {
      if (this.isEditMode) {
        this.validateAndConfirmSavingChanges();
      } else {
        this.isEditMode = true;
      }
    },
    updateDates: function (val: any, key: string) {
      this.$data.kennelInfo[key] = val?.date;
      this.$data.kennelInfo = Object.assign({}, this.$data.kennelInfo);
    },
    updateSelectedPeople: function (val: any) {
      //workaround for setting up the primary owner
      //and issues with reactivity in Vue https://v2.vuejs.org/v2/guide/reactivity.html?redirect=true#For-Arrays
      if(val.newPeople && (val.newPeople.length === 1 || (val.newPeople.length > 0 && val.newPeople.findIndex((own: any) => own.isPrimary) == -1)))
      {
        val.newPeople[0].isPrimary = true;
      }
      if(!!this.kennelInfo.owners && val.newPeople && val.newPeople.length > 0)
      {
        this.kennelInfo.owners.splice(val.newPeople.length);
      }
      this.$set(this.kennelInfo, 'owners', val.newPeople);
    },
  },
  created: function () {
    this.reloadData();
  },
  watch: {
    kennelId: function (newVal: String) {
      kennelService
        .getKennelById(this.kennelId)
        .then((res) => {
          this.kennelInfo = res;
        })

        .catch(this.$handleError);
    },
  },
  props: {
    kennelId: { type: String },
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    kennelOwnershipType: function () {
      return OwnershipType.Kennel;
    },
  },
});
</script>
